/**
 * Converts Firestore Timestamp, {seconds,nanoseconds}, ISO string, epoch ms/s, or Date to a valid Date.
 * @param {unknown} value
 * @returns {Date|null}
 */
export function parseFirestoreDate(value) {
  if (value == null || value === '') return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value.toDate === 'function') {
    try {
      const d = value.toDate();
      return d instanceof Date && !Number.isNaN(d.getTime()) ? d : null;
    } catch (_) {
      return null;
    }
  }
  if (typeof value === 'object') {
    const sec =
      typeof value.seconds === 'number'
        ? value.seconds
        : typeof value._seconds === 'number'
          ? value._seconds
          : null;
    if (sec != null) {
      const nano =
        typeof value.nanoseconds === 'number'
          ? value.nanoseconds
          : typeof value._nanoseconds === 'number'
            ? value._nanoseconds
            : 0;
      const d = new Date(sec * 1000 + nano / 1e6);
      return Number.isNaN(d.getTime()) ? null : d;
    }
  }
  if (typeof value === 'number') {
    const ms = value < 1e12 ? value * 1000 : value;
    const d = new Date(ms);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof value === 'string') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

/**
 * @param {unknown} value — Date-like, Firestore Timestamp, or epoch ms
 * @returns {string} en-GB date or em dash
 */
export function formatDateGB(value) {
  const d = parseFirestoreDate(value);
  if (!d) return '—';
  return d.toLocaleDateString('en-GB');
}

/** Best-effort patient signature date from consent document shape (Firestore / callable JSON). */
export function resolvePatientSignedDate(consent) {
  if (!consent) return null;
  const lastSig =
    Array.isArray(consent.signatures) && consent.signatures.length > 0
      ? consent.signatures[consent.signatures.length - 1]
      : null;
  const candidates = [
    consent.signedAt,
    lastSig?.timestamp,
    lastSig?.signedAt,
    consent.signatureData?.timestamp,
    consent.signatureData?.signedAt,
  ];
  for (const candidate of candidates) {
    const d = parseFirestoreDate(candidate);
    if (d) return d;
  }
  return null;
}

/** Best-effort clinician signature date from consent document shape. */
export function resolveClinicianSignedDate(consent) {
  const cs = consent?.clinicianSignature;
  if (!cs) return null;
  return parseFirestoreDate(cs.signedAt ?? cs.timestamp);
}

/** When the consent form was first created (not today's date). */
export function resolveConsentCreatedDate(consent) {
  if (!consent) return null;
  const candidates = [consent.createdAt, consent.metadata?.createdAt];
  for (const candidate of candidates) {
    const d = parseFirestoreDate(candidate);
    if (d) return d;
  }
  return null;
}
